import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../hook';
import { ClientConfirmDialog } from './ClientConfirmDialog';
import {
  archiveLead, createLead, getLeadDashboard, getLeads,
  getLeadSaleCandidates, getLeadSales, getLeadSources, getLeadStages,
  linkLeadSale, recordLeadContact, setLeadPriority, unlinkLeadSale, updateLead,
} from './clientApi';
import './client.scss';

const STATUS = {
  open: { label: 'Abierto', className: 'is-open' },
  won: { label: 'Ganado', className: 'is-won' },
  lost: { label: 'Perdido', className: 'is-lost' },
  archived: { label: 'Archivado', className: 'is-archived' },
};
const EMPTY_FORM = {
  title: '', company_name: '', contact_email: '', contact_phone: '', estimated_value: '0',
  stage_id: '', source_id: '', status: 'open', follow_up_at: '', notes: '', lost_reason: '',
  utm_source: '', utm_medium: '', utm_campaign: '', gclid: '', landing_page: '',
};
const money = (value) => `S/ ${Number(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateTime = (value) => value ? new Intl.DateTimeFormat('es-PE', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(value)) : '';
const monthLabel = (date) => new Intl.DateTimeFormat('es-PE', { month: 'long', year: 'numeric' }).format(date);
const monthValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const monthBounds = (date) => ({
  created_at_after: `${monthValue(date)}-01`,
  created_at_before: `${monthValue(date)}-${new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()}`,
});
const inputDateTime = (value) => value ? new Date(value).toISOString().slice(0, 16) : '';
const apiDateTime = (value) => value ? new Date(value).toISOString() : null;
const cleanPhone = (value) => {
  let phone = String(value || '').replace(/\D/g, '');
  if (phone.length === 9) phone = `51${phone}`;
  return phone;
};

function LeadFormDialog({ open, lead, stages, sources, saving, onClose, onSave, onArchive }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => {
    setForm(lead ? {
      ...EMPTY_FORM, ...lead,
      stage_id: lead.stage?.id || '', source_id: lead.source?.id || '',
      follow_up_at: inputDateTime(lead.follow_up_at),
    } : { ...EMPTY_FORM, stage_id: stages[0]?.id || '' });
    setAdvanced(false);
  }, [lead, open, stages]);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = () => onSave({
    ...form,
    estimated_value: form.estimated_value || '0',
    stage_id: Number(form.stage_id),
    source_id: form.source_id ? Number(form.source_id) : null,
    follow_up_at: apiDateTime(form.follow_up_at),
  });
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" className="client-lead-dialog">
    <DialogTitle>{lead ? 'Editar lead' : 'Nuevo lead'}</DialogTitle>
    <DialogContent>
      <div className="client-lead-form">
        <TextField label="Nombre del contacto o lead" value={form.title} onChange={set('title')} required />
        <TextField label="Empresa" value={form.company_name || ''} onChange={set('company_name')} />
        <div className="client-lead-form__row">
          <TextField label="WhatsApp" value={form.contact_phone || ''} onChange={set('contact_phone')} />
          <TextField label="Correo" type="email" value={form.contact_email || ''} onChange={set('contact_email')} />
        </div>
        <div className="client-lead-form__row">
          <TextField select label="Etapa" value={form.stage_id} onChange={set('stage_id')} required>
            {stages.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </TextField>
          <TextField select label="Estado" value={form.status} onChange={set('status')}>
            {Object.entries(STATUS).filter(([key]) => key !== 'archived').map(([key, item]) => <MenuItem key={key} value={key}>{item.label}</MenuItem>)}
          </TextField>
        </div>
        <div className="client-lead-form__row">
          <TextField select label="Fuente" value={form.source_id || ''} onChange={set('source_id')}>
            <MenuItem value="">Sin fuente</MenuItem>
            {sources.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </TextField>
          <TextField label="Valor estimado (S/)" type="number" value={form.estimated_value} onChange={set('estimated_value')} />
        </div>
        <TextField label="Próximo seguimiento" type="datetime-local" value={form.follow_up_at} onChange={set('follow_up_at')} InputLabelProps={{ shrink: true }} />
        {form.status === 'lost' && <TextField label="Motivo de pérdida" value={form.lost_reason || ''} onChange={set('lost_reason')} />}
        <TextField label="Notas" multiline minRows={3} value={form.notes || ''} onChange={set('notes')} />
        <Button className="client-lead-advanced" onClick={() => setAdvanced((value) => !value)}>
          {advanced ? 'Ocultar atribución' : 'Editar datos de campaña'}
        </Button>
        {advanced && <div className="client-lead-form__advanced">
          <div className="client-lead-form__row">
            <TextField label="UTM source" value={form.utm_source || ''} onChange={set('utm_source')} />
            <TextField label="UTM medium" value={form.utm_medium || ''} onChange={set('utm_medium')} />
          </div>
          <TextField label="Campaña" value={form.utm_campaign || ''} onChange={set('utm_campaign')} />
          <TextField label="Google Click ID" value={form.gclid || ''} onChange={set('gclid')} />
          <TextField label="Página de llegada" value={form.landing_page || ''} onChange={set('landing_page')} />
        </div>}
      </div>
    </DialogContent>
    <DialogActions className="client-lead-dialog__actions">
      {lead && <Button color="error" onClick={onArchive}>Archivar</Button>}
      <span />
      <Button onClick={onClose}>Cancelar</Button>
      <Button variant="contained" disabled={saving || !form.title.trim() || !form.stage_id} onClick={submit}>
        {saving ? 'Guardando...' : 'Guardar'}
      </Button>
    </DialogActions>
  </Dialog>;
}

function SalesDialog({ lead, token, open, onClose, onChanged }) {
  const [linked, setLinked] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const searchTerm = search.trim().length >= 3 ? search.trim() : '';
  const load = useCallback(async () => {
    if (!lead) return;
    setLoading(true);
    try {
      const [sales, available] = await Promise.all([
        getLeadSales(token, lead.id),
        getLeadSaleCandidates(token, lead.id, { page_size: 20, ...(searchTerm ? { search: searchTerm } : {}) }),
      ]);
      setLinked(sales); setCandidates(available.results || []);
    } finally { setLoading(false); }
  }, [lead, searchTerm, token]);
  useEffect(() => { if (open) load(); }, [load, open]);
  const toggle = async (sale) => {
    if (sale.lead_id === lead.id) await unlinkLeadSale(token, lead.id, sale.id);
    else await linkLeadSale(token, lead.id, sale.id);
    await load(); onChanged();
  };
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" className="client-lead-dialog">
    <DialogTitle>Ventas de {lead?.title}</DialogTitle>
    <DialogContent>
      <p className="client-lead-dialog__hint">Vincular una venta permite medir ingresos y ROI del lead.</p>
      {linked.length > 0 && <div className="client-lead-sales">
        <strong>Ventas vinculadas</strong>
        {linked.map((sale) => <div key={sale.id}><span>{sale.client_name || `Venta #${sale.id}`}<small>{sale.issue_date}</small></span><b>{money(sale.total_pen)}</b><Button onClick={() => toggle(sale)}>Quitar</Button></div>)}
      </div>}
      <TextField fullWidth label="Buscar venta o cliente" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ mt: 2 }} />
      {search.length > 0 && search.length < 3 && <p className="client-lead-dialog__hint">Escribe al menos 3 caracteres para buscar en todas las ventas.</p>}
      <div className="client-lead-sales">
        {loading ? <CircularProgress size={26} /> : candidates.filter((sale) => sale.lead_id !== lead?.id).map((sale) => <div key={sale.id}>
          <span>{sale.client_name || `Venta #${sale.id}`}<small>{sale.issue_date} · {sale.description || 'Sin descripción'}</small></span>
          <b>{money(sale.total_pen)}</b><Button onClick={() => toggle(sale)}>Vincular</Button>
        </div>)}
      </div>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
  </Dialog>;
}

function LeadDashboard({ token, cursor, onMove }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true; setData(null); setError('');
    getLeadDashboard(token, monthValue(cursor)).then((response) => active && setData(response)).catch((err) => active && setError(err.message));
    return () => { active = false; };
  }, [cursor, token]);
  return <>
    <div className="client-month-picker"><IconButton onClick={() => onMove(-1)}><ChevronLeftIcon /></IconButton><strong>{monthLabel(cursor)}</strong><IconButton onClick={() => onMove(1)}><ChevronRightIcon /></IconButton></div>
    {error && <Alert severity="error">{error}</Alert>}
    {!data ? <div className="client-empty"><CircularProgress size={28} /></div> : <div className="client-lead-dashboard">
      <section className="client-lead-kpis">
        <article><span>Leads</span><strong>{data.kpis.leads}</strong><small>{data.kpis.priority} prioritarios</small></article>
        <article><span>Conversión</span><strong>{data.kpis.conversion_rate || '0.00'}%</strong><small>{data.kpis.won} ganados · {data.kpis.lost} perdidos</small></article>
        <article><span>Inversión Ads</span><strong>{money(data.kpis.ad_spend_pen)}</strong><small>CPL {data.kpis.cost_per_lead ? money(data.kpis.cost_per_lead) : 'sin datos'}</small></article>
        <article><span>ROAS contratado</span><strong>{data.kpis.contracted_roas ? `${data.kpis.contracted_roas}x` : '—'}</strong><small>{money(data.kpis.contracted_revenue_pen)} vendidos</small></article>
        <article><span>Costo por cliente</span><strong>{data.kpis.customer_acquisition_cost ? money(data.kpis.customer_acquisition_cost) : '—'}</strong><small>Inversión por lead ganado</small></article>
        <article><span>ROAS cobrado</span><strong>{data.kpis.cash_roas ? `${data.kpis.cash_roas}x` : '—'}</strong><small>{money(data.kpis.collected_revenue_pen)} cobrados</small></article>
      </section>
      <section className="client-lead-panel"><h2>Embudo del mes</h2>{data.funnel.map((item) => <div className="client-lead-funnel" key={item.stage__id}><i style={{ background: item.stage__color }} /><span>{item.stage__name}</span><b>{item.count}</b></div>)}</section>
      <section className="client-lead-panel"><h2>Rendimiento por campaña</h2>{data.campaigns.length ? data.campaigns.map((item) => <div className="client-lead-campaign" key={item.campaign}><span><b>{item.campaign}</b><small>{item.leads} leads · {item.won} ganados · inversión {money(item.spend_pen)}</small><small>CPL {item.cost_per_lead ? money(item.cost_per_lead) : '—'} · ROAS {item.roas ? `${item.roas}x` : '—'}</small></span><strong>{money(item.revenue_pen)}</strong></div>) : <p className="client-lead-dialog__hint">Aún no hay campañas atribuidas en este mes.</p>}</section>
      {!Number(data.kpis.ad_spend_pen) && <Alert severity="info">Carga la inversión de Google Ads en “Rendimiento de campañas” del Admin para obtener CPL, CAC y ROAS.</Alert>}
    </div>}
  </>;
}

export function Leads() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { auth } = useAuth();
  const [tab, setTab] = useState('all');
  const [cursor, setCursor] = useState(() => new Date());
  const [monthActive, setMonthActive] = useState(false);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, results: [], next: null, previous: null });
  const [stages, setStages] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [salesLead, setSalesLead] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  useEffect(() => { Promise.all([getLeadStages(auth.token), getLeadSources(auth.token)]).then(([stageRows, sourceRows]) => { setStages(stageRows); setSources(sourceRows); }).catch((err) => setError(err.message)); }, [auth.token]);
  useEffect(() => {
    if (searchParams.get('nuevo') === '1' && stages.length) {
      setSelected(null); setFormOpen(true);
      navigate('/client/leads', { replace: true });
    }
  }, [navigate, searchParams, stages]);
  const params = useMemo(() => ({
    page, page_size: 20, ordering: '-created_at',
    ...(search ? { search } : {}),
    ...(tab === 'priority' ? { is_priority: 'true' } : {}),
    ...(monthActive ? monthBounds(cursor) : {}),
  }), [cursor, monthActive, page, search, tab]);
  const load = useCallback(() => {
    if (tab === 'dashboard') return Promise.resolve();
    setLoading(true); setError('');
    return getLeads(auth.token, params).then(setData).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [auth.token, params, tab]);
  useEffect(() => { load(); }, [load]);
  const changeTab = (next) => { setTab(next); setPage(1); };
  const moveMonth = (amount) => { setCursor((value) => new Date(value.getFullYear(), value.getMonth() + amount, 1)); setMonthActive(true); setPage(1); };
  const submitSearch = (event) => { event.preventDefault(); setSearch(query.trim()); setMonthActive(false); setPage(1); };
  const openNew = () => { setSelected(null); setFormOpen(true); };
  const openEdit = (lead) => { setSelected(lead); setFormOpen(true); };
  const save = async (payload) => {
    setSaving(true); setError('');
    try {
      if (selected) await updateLead(auth.token, selected.id, payload); else await createLead(auth.token, payload);
      setFormOpen(false); await load();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  const quickStatus = async (event, lead) => { event.stopPropagation(); await updateLead(auth.token, lead.id, { status: event.target.value }); await load(); };
  const priority = async (event, lead) => { event.stopPropagation(); await setLeadPriority(auth.token, lead.id, !lead.is_priority); await load(); };
  const whatsapp = async (event, lead) => {
    event.stopPropagation(); const phone = cleanPhone(lead.contact_phone); if (!phone) return;
    window.open(`https://wa.me/${phone}`, '_blank', 'noopener,noreferrer');
    recordLeadContact(auth.token, lead.id, { activity_type: 'whatsapp', description: 'Contacto por WhatsApp' }).catch(() => {});
  };
  const confirmArchive = async () => { await archiveLead(auth.token, selected.id); setArchiveOpen(false); setFormOpen(false); await load(); };
  const pages = Math.max(1, Math.ceil((data.count || 0) / 20));

  return <main className="client-page client-leads">
    <header className="client-header"><IconButton onClick={() => navigate('/')}><ArrowBackIosNewIcon /></IconButton><h1>Leads</h1><IconButton className="client-lead-add" onClick={openNew}><AddIcon /></IconButton></header>
    <div className="client-lead-tabs"><Button className={tab === 'all' ? 'is-active' : ''} onClick={() => changeTab('all')}>Todos</Button><Button className={tab === 'priority' ? 'is-active' : ''} onClick={() => changeTab('priority')}>Prioritarios</Button><Button className={tab === 'dashboard' ? 'is-active' : ''} onClick={() => changeTab('dashboard')}>Dashboard</Button></div>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {tab === 'dashboard' ? <LeadDashboard token={auth.token} cursor={cursor} onMove={moveMonth} /> : <>
      <form className="client-lead-search" onSubmit={submitSearch}><TextField placeholder="Buscar nombre, empresa, teléfono..." value={query} onChange={(event) => setQuery(event.target.value)} /><Button type="submit">Buscar</Button></form>
      {(search || monthActive) && <div className="client-lead-filter-note"><span>{search ? `Búsqueda: “${search}”` : monthLabel(cursor)}</span><Button onClick={() => { setSearch(''); setQuery(''); setMonthActive(false); setPage(1); }}>Borrar filtro</Button></div>}
      {loading ? <div className="client-empty"><CircularProgress size={28} /></div> : <section className="client-lead-list">
        {data.results.map((lead) => <article key={lead.id} className={`client-lead-card ${STATUS[lead.status]?.className || ''}`} onClick={() => openEdit(lead)}>
          <IconButton className={`client-lead-star${lead.is_priority ? ' is-active' : ''}`} onClick={(event) => priority(event, lead)}>{lead.is_priority ? <StarIcon /> : <StarBorderIcon />}</IconButton>
          <div className="client-lead-card__main"><strong>{lead.title}</strong><span>{lead.company_name || lead.contact_phone || 'Sin empresa'}</span><small>{dateTime(lead.created_at)}{lead.follow_up_at ? ` · Seguimiento ${dateTime(lead.follow_up_at)}` : ''}</small></div>
          <div className="client-lead-card__aside"><select value={lead.status} onClick={(event) => event.stopPropagation()} onChange={(event) => quickStatus(event, lead)}>{Object.entries(STATUS).filter(([key]) => key !== 'archived').map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}</select><b>{money(lead.attributed_revenue_pen || lead.estimated_value)}</b><small>{lead.sales_count ? `${lead.sales_count} venta(s)` : lead.stage?.name}</small></div>
          {lead.contact_phone && <IconButton className="client-lead-whatsapp" onClick={(event) => whatsapp(event, lead)}><WhatsAppIcon /></IconButton>}
          <Button className="client-lead-link-sale" onClick={(event) => { event.stopPropagation(); setSalesLead(lead); }}>Ventas</Button>
        </article>)}
        {!data.results.length && <div className="client-empty">No hay leads para mostrar.</div>}
      </section>}
      {pages > 1 && <div className="client-lead-pagination"><Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Anterior</Button><span>{page} de {pages}</span><Button disabled={page >= pages} onClick={() => setPage((value) => value + 1)}>Siguiente</Button></div>}
      <div className="client-lead-month-area"><div className="client-month-picker"><IconButton onClick={() => moveMonth(-1)}><ChevronLeftIcon /></IconButton><strong>{monthLabel(cursor)}</strong><IconButton onClick={() => moveMonth(1)}><ChevronRightIcon /></IconButton></div>{monthActive && <Button onClick={() => { setMonthActive(false); setPage(1); }}>Borrar filtro de fecha</Button>}</div>
    </>}
    <LeadFormDialog open={formOpen} lead={selected} stages={stages} sources={sources} saving={saving} onClose={() => setFormOpen(false)} onSave={save} onArchive={() => setArchiveOpen(true)} />
    <SalesDialog open={Boolean(salesLead)} lead={salesLead} token={auth.token} onClose={() => setSalesLead(null)} onChanged={load} />
    <ClientConfirmDialog open={archiveOpen} title="¿Archivar este lead?" message="Dejará de aparecer en la lista activa, pero conservará su historial y sus ventas." confirmText="Sí, archivar" onCancel={() => setArchiveOpen(false)} onConfirm={confirmArchive} />
  </main>;
}
